import React, { useEffect, useState } from 'react';
import { Image, Modal, Alert, FlatList, Platform } from 'react-native';
import { Icons } from '../../Components';
import Colors from '../../constants/colors';
import { ProfileApi, ProfileHelpers } from '../api';
import moment from 'moment-mini';
import type { CodeRequestProps, MyPlayerProps, PlayerReferralProps, PromoProps, PublicPlayerProps } from '../../types';
import { Button, Text, View } from '../../Components/Themed';
import { useColors } from '../../constants/useColors';
import WebView from 'react-native-webview';
import type { PlayerTokenProps } from '../../Authenticator/api/types';
import BettorVouch from './BettorVouch';

type VouchCardProps = {
    player:MyPlayerProps,
    walkthrough?:boolean,
    view_mode?:'desktop'|'mobile',
    insets?:{ top:number, bottom:number, left?:number, right?:number },
    onVouching:(active:boolean) => void,
    code_details: {
        code_request?:CodeRequestProps,
        referrer?:PublicPlayerProps,
        promo?:PromoProps,
        player_referral?:PlayerReferralProps
    }
    onPlayerUpdate:(player:MyPlayerProps) => void,
    onClaimGift:() => void,
    onClose:() => void
}
const sections = ['header','info','image','disclaimer','code','failed']
const VouchCard = ({ player, view_mode, walkthrough, insets, code_details, onVouching, onPlayerUpdate, onClaimGift, onClose }:VouchCardProps) => {
    const C = useColors();
    const [ vouching, setVouching ] = useState(false);
    const [ vouch_loading, setVouchLoading ] = useState(false);
    const [ player_token, setPlayerToken ] = useState<PlayerTokenProps|undefined>(undefined);
    const [ disclaimer_confirmed, setDisclaimerConfirmed ] = useState(false);
    const [ id_disclaimer_confirmed, setIDDisclaimerConfirmed ] = useState(false);
    const [ info_visible, setInfoVisible ] = useState(false);
    const { code_request, promo } = code_details;
    useEffect(() => {
        getPlayerFromServer()
        if(Platform.OS != 'web'){ return }
        const script = document.createElement('script');
        script.src ="https://static.vouched.id/plugin/releases/latest/index.js"
        script.async = true;
        document.body.appendChild(script);
        return () => {
            document.body.removeChild(script); // Cleanup on component unmount
          }
    },[]);

    const getPlayerFromServer = async() => {
        const me = await ProfileApi.getMyDetails();
        onPlayerUpdate(me.player)
    }

    useEffect(() => {
        if(onVouching){ onVouching(vouching) }
    },[vouching])


    const verifyErrors = (p:MyPlayerProps) => {
        let errors:string[] = []
        if(!p.first_name || !p.last_name){ errors.push('Please include your name.  This must match your ID exactly.') }
        if(!p.dob || p.dob == ''){ errors.push('Please include your date of birth. This must match your ID exactly.') }
        if(p.dob){ 
            let age_diff = moment().diff(moment(p.dob), 'years')
            if(age_diff < 18){ errors.push('Must be over the age of 18 to participate in real money activities on BettorEdge') }
        }
        if(!p.verified){ errors.push('A valid and verified email must be associated with your account before we can verify') }
        if(!p.phone_verified){ errors.push('A valid and verified phone number must be associated with your account before we can verify') }
        if(p.no_password){ errors.push('Please add a password for your account before you continue to verify') }
        return errors
    }


    const startVouchingPlayer = async() => {
        setVouchLoading(true);
        const me = await ProfileApi.getMyDetails();
        if(!['unverified','reverify'].includes(me.player.vouched_status)){
            return onPlayerUpdate(me.player);
        }
        if(Platform.OS != 'web'){
            //Only need to get this if not on web
            const player_token = await ProfileApi.getShortToken();
            if(!player_token){
                return Alert.alert('Unable to process at this time')
            }
            setPlayerToken(player_token);
        }
        setVouchLoading(false);
        setVouching(true);
    }



    const verify_errors = verifyErrors(player)
    let scan_ready = disclaimer_confirmed && id_disclaimer_confirmed ? true : false
    let dob = player?.dob ?? ''
    let split_dob = dob.split('T')
    if(split_dob[0]){
        split_dob = split_dob[0].split('-')
    }
    let new_date = `${split_dob[1]}/${split_dob[2]}/${split_dob[0]}`

    const renderSections = (data:{item:string, index:number}) => {
        switch(data.item){
            case 'header':
                return (
                    <View style={{ padding:20 }}>
                        <Text size={26} theme='h1'>Help us identify you</Text>
                        <Text style={{ marginTop:15 }} size={14} theme='h2'>We need to take extra steps to verify your account. Scan your Driver's License or state issued ID and take a selfie for instant verification</Text>
                    </View>
                )
            case 'info':
                return (
                    <Button style={{padding:10, borderRadius:8, backgroundColor:C.views.gold, margin:20}} onPress={() => setInfoVisible(!info_visible)}>
                        <View transparent style={{ flexDirection:'row', alignItems:'center' }}>
                            <Text style={{ flex:1 }} theme='h1'>Why is this step required?</Text>
                            <Icons.ChevronIcon direction={info_visible?'up':'down'} size={10} color={C.text.h1} />
                        </View>
                        {info_visible ?
                        <Text theme='description' style={{ marginTop:4 }}>When betting with real money, legally, BettorEdge has to verify your identity to make sure you are old enough to bet and that you are who you say you are.</Text>
                        :<></>}
                </Button>
                )
            case 'image':
                return (
                    <View style={{ justifyContent:'center', alignItems:'center' }}>
                        <Image 
                            source={{ uri: 'https://res.cloudinary.com/hoabts6mc/image/upload/v1640215120/h4_2_media_fyiuoy.webp' }} 
                            style={{ height: 150, width:300 }}
                            resizeMode='contain'
                            />
                    </View>
                )
            case 'disclaimer':
                if(!['unverified','reverify'].includes(player.vouched_status)){ return <></> }
                return (
                    <View style={{ padding:10 }}>
                        <Button 
                            style={{ padding:0 }}
                            onPress={() => setDisclaimerConfirmed(!disclaimer_confirmed)}
                        >
                            <View float style={{ padding:10, flexDirection:'row', alignItems:'center' }}>
                                <View transparent style={{ justifyContent:'center', alignItems:'center', borderWidth:1, borderColor:C.text.h1, borderRadius:4, height:20, width:20 }}>
                                    {disclaimer_confirmed ?
                                    <Icons.CheckIcon size={14} color={C.text.h1} />
                                    :<></>}
                                </View>
                                <Text style={{ marginLeft:10, flex:1 }} theme='h2'>I confirm that my name is <Text size={14} theme='warning'>{player.first_name} {player.last_name}</Text> and that my date of birth is <Text size={14} theme='warning'>{new_date}</Text>. Mismatch will cause failure.</Text>
                            </View>
                        </Button>
                        <Button 
                            onPress={() => setIDDisclaimerConfirmed(!id_disclaimer_confirmed)}
                            style={{ padding:0 }}

                        >
                            <View float style={{  marginTop:10, padding:10, flexDirection:'row', alignItems:'center' }}>
                                <View transparent style={{ justifyContent:'center', alignItems:'center', borderWidth:1, borderColor:C.text.h1, borderRadius:4, height:20, width:20 }}>
                                    {id_disclaimer_confirmed ?
                                    <Icons.CheckIcon size={14} color={C.text.h1} />
                                    :<></>}
                                </View>
                                <Text style={{ marginLeft:10, flex:1 }} theme='h2'>I confirm that I am using a valid <Text size={14} theme='warning'>(Not Expired)</Text> US government issued ID. Expired ID will cause failure.</Text>
                            </View>
                        </Button>
                    </View>
                )
            case 'failed':
                if(player.vouched_status != 'failed'){ return <></> }
                return (
                    <View style={{ margin:10, borderRadius:8, padding:10, backgroundColor:Colors.utility.warning }}>
                        <Text  textAlign='center' size={14} weight='bold' color={Colors.shades.white}>We are currently reviewing your account.  If you do not hear from us within 48 hours, please reach out to support@bettoredge.com</Text>
                    </View>
                )
            case 'code':
                if(!code_request || !promo){ return <></> }
                if(['closed','fulfilled'].includes(code_request.status)){ return <></> }
                return (
                    <View>
                        {player.vouched_status == 'verified' ?
                        <Button 
                            float
                            disabled={player.vouched_status!='verified' ? true : false}
                            onPress={() => onClaimGift()}
                            style={{flexDirection:'row', alignItems:'center', justifyContent:'center', alignSelf:'center' }}
                            >
                            <Text size={14} color={Colors.incentive.gold} weight='bold'>CLAIM ME NOW!</Text>
                            <Icons.ChevronIcon direction='down' size={8} color={Colors.incentive.gold} />
                        </Button>
                        :<></>}
                        <Button 
                            float 
                            style={{ padding:0, margin:10, flexDirection:'row', backgroundColor:Colors.incentive.gold }}
                            disabled={player.vouched_status != 'verified' ? true : false}
                            onPress={() => onClaimGift()}
                            >
                        
                            <View transparent style={{ padding:10, justifyContent:'center', alignItems:'center' }}>
                                <Icons.GiftIcon size={40} color={C.text.white} />
                                {player.vouched_status == 'verified' ? 
                                <Text style={{ marginTop:5 }} textAlign='center' size={12} color={C.text.gold}>CLAIM NOW</Text>
                                :<></>}
                            </View>
                            <View transparent style={{flex:1, padding:10 }}>
                                <Text theme='h1'>{player.vouched_status == 'verified'?`Congrats! Press Here to claim your gift!`: 'Almost there! Last step to get your gift!'}</Text>
                                <Text style={{ paddingTop:5 }} theme='h2'>{ProfileHelpers.getReferralDescription(promo)}</Text>
                            </View>
                            
                        </Button>
                    </View>
                   
                )
            default: return <></>
        }
    }

    return (
        <View style={{ flex:1 }}>
            <View style={{ flex:1 }}>
                <FlatList
                    data={sections}
                    key={'vouch_sections'}
                    keyExtractor={(item) => item}
                    renderItem={renderSections}
                />
            </View>
            {verify_errors.length > 0 ?
            <View style={{ padding:10 }}>
                <View type='footer' style={{ padding:20, borderRadius:8 }}>
                    <View transparent style={{ padding:10 }}>
                        <Text size={18} theme='h1'>ERRORS:</Text>
                        <Text style={{ marginTop:5 }} theme='description'>Please fix the following before continuing to verify</Text>
                    </View>
                    <View float style={{ padding:10, borderRadius:8 }}>
                        {verify_errors.map((e,i) => {
                            return (
                                <View transparent style={{ padding:5, flexDirection:'row', alignItems:'center' }}>
                                    <Text size={12} color={Colors.utility.warning} weight='bold'>{i+1} - {e}</Text>
                                </View>
                            )
                        })}
                    </View>
                </View>
            </View>
            :<></>}
            <View nativeID='action_row' style={{ flexDirection:'row', padding:10 }}>   
                {walkthrough ?
                <Button
                    title='EXIT SETUP'
                    title_color={Colors.utility.error}
                    backgroundColor='transparent'
                    onPress={() => onClose()}
                />
                :
                <Button
                    style={{ flex:1, marginRight:5 }}
                    title='BACK'
                    type='close'
                    onPress={() => onClose()}
                />
                }
                {player.vouched_status == 'unverified' ?
                <Button
                    title={'START'}
                    loading={vouching || vouch_loading}
                    disabled={!scan_ready || vouch_loading || verify_errors.length > 0}
                    style={{ flex:3, opacity:scan_ready && scan_ready&&!vouching&&verify_errors.length == 0?1:0.5 }}
                    type='success'
                    onPress={() => startVouchingPlayer()}
                />
                : player.vouched_status == 'reverify' ? 
                <Button
                    title={'REVERIFY NOW'}
                    loading={vouching || vouch_loading}
                    disabled={!scan_ready || vouch_loading || verify_errors.length > 0}
                    style={{ flex:3, opacity:scan_ready && scan_ready&&!vouching&&verify_errors.length == 0?1:0.5 }}
                    type='success'
                    onPress={() => startVouchingPlayer()}
                />
                : player.vouched_status == 'failed' ?
                <View style={{ flex:3, margin:4, borderRadius: 8, padding:10, backgroundColor:Colors.shades.shade100 }}>
                    <Text size={16} color={Colors.brand.midnight} textAlign='center' weight='semibold'>Account In Review</Text>
                </View>
                :
                <View style={{ flex:3, margin:4, borderRadius: 8, padding:10, backgroundColor:Colors.highlights.highlight500 }}>
                    <Text size={16} color={Colors.brand.midnight} textAlign='center' weight='semibold'>You are verified!</Text>
                </View>
                }
            </View>
            
            <Modal
                visible={['unverified','reverify'].includes(player?.vouched_status) && vouching ? true : false}
                transparent
                animationType={view_mode == 'desktop' ? 'fade' : 'slide'}
                style={{flex:1}}
            >
                <View type='blur' style={view_mode == 'desktop'?{ flex:1, justifyContent:'center', alignItems:'center' }:{ flex:1 }}>

                <View style={view_mode == 'desktop'?{ flex:1, width:500, maxHeight:700 }:{flex:1, padding:10, paddingBottom:insets?.bottom, paddingTop:insets?.top}}>
                    <View float style={{flex:1}} >
                        <View type='header' style={{ flexDirection:'row', padding:10, borderTopRightRadius:8, borderTopLeftRadius: 8}}>
                            <View transparent style={{flex:1}}>
                                <Text theme='h1'>Verifying Your Identity</Text>
                                <Text style={{marginTop:4}} theme='description'>Use the prompts below to verify.  Please reach out to support if you have any issues.</Text>
                            </View>
                        </View>
                    {(player?.vouched_status == 'unverified'||player?.vouched_status == 'reverify') && vouching ?
                    <View style={{ flex:1 }}>
                    {Platform.OS == 'web' ?
                        <View style={{ flex:1 }}>
                        <BettorVouch 
                            first_name={player.first_name}
                            last_name={player.last_name}
                            email={player.email}
                            phone={player.phone}
                            VOUCHED_CALLBACK_URL='https://be-auth-svc.herokuapp.com/v1/players/vouch/hook'
                        />
                        </View>
                    : player_token ?
                        <View style={{flex:1}}>
                            <WebView
                                source={{ uri: `https://play.bettoredge.com/account/verify?token=${player_token.token}` }}
                                geolocationEnabled={true}
                                mediaPlaybackRequiresUserAction={false}
                                allowsInlineMediaPlayback
                                javaScriptEnabled={true}
                            />
                        </View>
                    :<></>}
                    </View>
                    :<></>}
                    <View type='footer' style={{ flexDirection:'row', alignItems:'center', padding:10 }}>
                        <Button
                            type='error'
                            title='CANCEL'
                            style={{ flex:1 }}
                            onPress={() => setVouching(false)}
                        />
                    </View>
                    </View>
                </View>
                </View>
            </Modal>
        </View>
    )
}

export default VouchCard